<template>
  <div>
    <v-card class="mb-5" variant="flat">
      <v-card-title>
        <div class="flex">
          Customer Emails
          <v-spacer/>
          <v-btn size="small" @click="domainDialog = true; createEmailDomainErrorMessage = '';">
            <v-icon class="mr-1" size="small">mdi-plus</v-icon>
            Add domain
          </v-btn>
        </div>
      </v-card-title>
      <v-card-subtitle>
        Customize the domain used when sending emails to customers.
        <TheHelpLink url="https://help.fleetwire.io/en/article/custom-email-domain-19fqufb/?bust=1649702303369"></TheHelpLink>
      </v-card-subtitle>
      <v-card-text>
        <div class="table-responsive-sm">
          <table class="w-full text-sm text-left text-gray-500 dark:text-gray-400">
            <thead class="text-xs text-gray-700 uppercase bg-gray-50 dark:bg-gray-700 dark:text-gray-400">
            <tr>
              <th class="px-6 py-3" scope="col">Domain</th>
              <th class="px-6 py-3" scope="col">Email</th>
              <th class="px-6 py-3" scope="col">DKIM Verified</th>
              <th class="px-6 py-3" scope="col">SPF Verified</th>
              <th class="px-6 py-3" scope="col"></th>
              <th class="px-6 py-3" scope="col">
                <span class="sr-only">Actions</span>
              </th>
            </tr>
            </thead>
            <tbody>
            <tr v-for="item in domains" :key="item.name" class="bg-white border-b dark:bg-gray-800 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600">
              <th class="px-6 py-4 font-medium text-gray-900 whitespace-nowrap dark:text-white" scope="row">
                {{ item.domain }}
                <div v-if="showSending(item)" class="items-center text-blue-100 leading-none lg:rounded-full flex lg:inline-flex" role="alert">
                  <span class="flex rounded-full bg-blue-500 uppercase px-2 py-1 text-xs font-bold">Sending</span>
                </div>
              </th>
              <td class="px-6 py-4" style="cursor: text;">
                <span v-if="item.email === 'no-reply@fleetwire.io'">{{ item.email }}</span>
                <span v-else-if="!editEmail || editEmail !== item" @click="editEmail = item"><v-icon size="small">mdi-pencil-outline</v-icon> {{
                    item.email
                  }}</span>
                <v-text-field
                  v-if="editEmail && editEmail.uuid === item.uuid && item.email !== 'no-reply@fleetwire.io'"
                  v-model="username"
                  :append-icon="'mdi-content-save'"
                  autofocus
                  class="m-0"
                  density="compact"
                  label="Username"
                  variant="outlined"
                  @blur="editEmail = false"
                  @click:append="updateEmailAddress(item)"
                  @keyup.enter="updateEmailAddress(item); $event.target.blur()"
                ></v-text-field>
              </td>
              <td class="px-6 py-4">
                <div v-if="item.DKIMVerified">
                  <div class="items-center text-green-100 leading-none lg:rounded-full flex lg:inline-flex" role="alert">
                    <span class="flex rounded-full bg-green-500 uppercase px-2 py-1 text-xs font-bold">Yes</span>
                  </div>
                </div>
                <div v-else>
                  <div class="items-center text-indigo-100 leading-none lg:rounded-full flex lg:inline-flex" role="alert" @click="emailDomainCheckDKIM(item)">
                    <span class="flex rounded-full bg-red-500 uppercase px-2 py-1 text-xs font-bold mr-3">No</span>
                    <svg class="fill-current opacity-75 h-4 w-4" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.95 10.707l.707-.707L8 4.343 6.586 5.757 10.828 10l-4.242 4.243L8 15.657l4.95-4.95z"/>
                    </svg>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <div v-if="item.SPFVerified">
                  <div class="items-center text-green-100 leading-none lg:rounded-full flex lg:inline-flex" role="alert">
                    <span class="flex rounded-full bg-green-500 uppercase px-2 py-1 text-xs font-bold">Yes</span>
                  </div>
                </div>
                <div v-else>
                  <div class="items-center text-indigo-100 leading-none lg:rounded-full flex lg:inline-flex" role="alert">
                    <span class="flex rounded-full bg-red-500 uppercase px-2 py-1 text-xs font-bold mr-3">No</span>
                    <svg class="fill-current opacity-75 h-4 w-4" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12.95 10.707l.707-.707L8 4.343 6.586 5.757 10.828 10l-4.242 4.243L8 15.657l4.95-4.95z"/>
                    </svg>
                  </div>
                </div>
              </td>
              <td class="px-6 py-4">
                <v-btn v-if="showSetAsDefault(item)" class="mr-3" size="x-small" @click="setAsDefaultSendingDomain(item)">
                  Set as sending domain
                </v-btn>
              </td>
              <td class="table-action px-6 py-4 text-right">
                <v-menu location="bottom">
                  <template v-slot:activator="{ props }">
                    <v-icon color="grey" v-bind="props">mdi-dots-vertical</v-icon>
                  </template>
                  <v-list>
                    <v-list-item :disabled="!item.active && item.domain !== 'fleetwire.io'" @click="sendTestEmailInit()">
                      <v-list-item-title>Send Test Email</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.domain !== 'fleetwire.io'" @click="domainSettingsInit(item)">
                      <v-list-item-title>View Domain Settings</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.domain !== 'fleetwire.io'" :disabled="item.active" @click="domainSettingsRemoveInit(item)">
                      <v-list-item-title>Delete</v-list-item-title>
                    </v-list-item>
                    <v-divider v-if="item.domain !== 'fleetwire.io'"/>

                    <v-list-item v-if="item.domain !== 'fleetwire.io'  && !item.ReturnPathDomainVerified" @click="emailDomainCheckReturnPathStatus(item)">
                      <v-list-item-title>Check Return Path Status</v-list-item-title>
                    </v-list-item>
                    <v-list-item v-if="item.domain !== 'fleetwire.io'  && !item.DKIMVerified" @click="emailDomainCheckDKIM(item)">
                      <v-list-item-title>Check DKIM Status</v-list-item-title>
                    </v-list-item>
                  </v-list>
                </v-menu>
              </td>
            </tr>
            </tbody>
          </table>
        </div>
      </v-card-text>
    </v-card>
    <v-divider/>

    <EmailTemplates/>
    <v-divider/>

    <v-dialog v-model="domainDialog" width="400">
      <v-card>
        <v-card-title>Add your email domain</v-card-title>
        <v-divider opacity="50"/>
        <v-card-text class="">
          <v-text-field
            v-model="newDomain"
            :error="createEmailDomainErrorMessage.length > 0"
            label="Email Domain"
            placeholder="example.com or subdomain.example.com"
            variant="outlined"
            @keyup.enter="createEmailDomain(newDomain); $event.target.blur()"
          ></v-text-field>

          <div v-show="createEmailDomainErrorMessage" class="bg-red-100 border border-eed-400 text-red-700 px-4 py-3 rounded relative" role="alert">
            <strong class="font-bold mr-2">Error!</strong>
            <span class="block sm:inline">{{ createEmailDomainErrorMessage }}</span>
            <span class="absolute top-0 bottom-0 right-0 px-4 py-3" @click="createEmailDomainErrorMessage = ''">
              <svg class="fill-current h-6 w-6 text-red-500" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><title>Close</title><path d="M14.348 14.849a1.2 1.2 0 0 1-1.697 0L10 11.819l-2.651 3.029a1.2 1.2 0 1 1-1.697-1.697l2.758-3.15-2.759-3.152a1.2 1.2 0 1 1 1.697-1.697L10 8.183l2.651-3.031a1.2 1.2 0 1 1 1.697 1.697l-2.758 3.152 2.758 3.15a1.2 1.2 0 0 1 0 1.698z"/></svg>
            </span>
          </div>
        </v-card-text>
        <v-divider opacity="50"/>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="newDomain.length < 4" :loading="emailDomainSettingsLoading.creatingEmailDomain" color="primary" @click="createEmailDomain(newDomain); createEmailDomainErrorMessage = ''">
            Add domain
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="testEmailDialog" width="400">
      <v-card>
        <v-card-title>
          Send Test email from {{ activeDomain }}
        </v-card-title>
        <v-divider opacity="50"/>
        <v-card-text>
          <v-text-field
            v-model="testEmailAddress"
            label="email"
            name="email"
            variant="outlined"
          ></v-text-field>
        </v-card-text>
        <v-divider opacity="50"/>
        <v-card-actions>
          <v-spacer/>
          <v-btn :disabled="testEmailAddress.length < 4" :loading="emailDomainSettingsLoading.sendingTestEmail" color="primary" @click="sendTestEmail(testEmailAddress)">
            Send Test Email
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="domainSettingsDialog" width="550">
      <v-card>
        <v-card-title>DNS Records</v-card-title>

        <v-card-subtitle>
          Here are the 3 records you should add to your DNS settings.
          <TheHelpLink url="https://help.fleetwire.io/en/article/custom-email-domain-19fqufb/?bust=1658353515728"/>
        </v-card-subtitle>
        <v-card-text class="mt-4">
          <v-expansion-panels multiple>
            <v-expansion-panel>
              <v-expansion-panel-title>
                <template v-slot:default="{ expanded }">
                  <div class="">
                    <div class="col-4"><span>DKIM</span></div>
                    <div class="col-8 text--secondary">
                      <v-fade-transition leave-absolute>
                        <span v-if="expanded">Type: TXT</span>
                        <div v-else class="" style="width: 100%">
                          <div class="col-6">
                            <span v-if="currentWorkingSetting.DKIMUpdateStatus === 'Verified'" class="text-sm text-green-600 flex items-center float-right">
                              <svg enable-background="new 0 0 24 24" height="15" viewBox="0 0 24 24" width="15" xmlns="http://www.w3.org/2000/svg"><path d="m17.5 24c-3.584 0-6.5-2.916-6.5-6.5s2.916-6.5 6.5-6.5 6.5 2.916 6.5 6.5-2.916 6.5-6.5 6.5zm0-11.5c-2.757 0-5 2.243-5 5s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"/><path d="m16.5 20c-.198 0-.39-.079-.53-.22l-2-2c-.293-.293-.293-.768 0-1.061s.768-.293 1.061 0l1.434 1.433 2.972-3.396c.274-.313.747-.343 1.059-.071.312.273.343.747.07 1.058l-3.5 4c-.138.157-.334.25-.541.257-.008 0-.017 0-.025 0z"/><path d="m8.5 21c-.048 0-.096-.004-.143-.014-.084-.016-8.357-1.725-8.357-11.056v-6.18c0-.31.19-.587.479-.699l7.75-3c.174-.067.367-.067.541 0l7.75 3c.29.112.48.389.48.699v4.5c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-3.986l-7-2.709-7 2.709v5.666c0 7.594 6.01 9.32 7.012 9.555h.002c.406-.086.801.182.882.589.081.406-.183.801-.589.882l-.186.034c-.04.007-.08.01-.121.01z"/></svg>
                              <span>Verified</span>
                            </span>
                            <span v-else class="text-sm text-yellow-600 flex items-center float-right">
                              <svg id="Capa_1" enable-background="new 0 0 512 512" height="15" viewBox="0 0 512 512" width="15" xmlns="http://www.w3.org/2000/svg"><g><path d="m392.274 204.411c-37.588 0-71.18 17.417-93.146 44.599l-30.294-29.083 129.032-123.87c1.875 3.468 2.941 7.434 2.941 11.645v72.145c0 4.142 3.358 7.5 7.5 7.5s7.5-3.358 7.5-7.5v-72.145c0-21.816-17.749-39.564-39.565-39.564h-336.678c-21.815-.001-39.564 17.748-39.564 39.564v224.452c0 21.816 17.749 39.564 39.564 39.564h216.436c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5h-216.436c-3.876 0-7.543-.906-10.806-2.511l129.275-124.104c14.57 11.748 32.216 17.637 49.87 17.637s35.3-5.889 49.87-17.637l32.583 31.28c-11.284 18.26-17.808 39.759-17.808 62.755 0 66.017 53.709 119.726 119.726 119.726s119.726-53.71 119.726-119.727-53.708-119.726-119.726-119.726zm-229.14 10.238-16.006-15.366c-2.988-2.869-7.736-2.772-10.604.216s-2.771 7.736.217 10.604l10.232 9.823-129.032 123.872c-1.875-3.468-2.941-7.434-2.941-11.645v-224.451c0-4.211 1.066-8.177 2.941-11.645l93.952 90.194c1.454 1.396 3.325 2.09 5.193 2.09 1.971 0 3.939-.772 5.412-2.306 2.868-2.988 2.771-7.736-.217-10.604l-93.523-89.783c3.263-1.605 6.93-2.511 10.806-2.511h336.677c3.876 0 7.543.906 10.807 2.511l-134.376 129.001c-25.104 24.101-64.433 24.101-89.538 0zm229.14 214.214c-57.746 0-104.726-46.98-104.726-104.726s46.98-104.726 104.726-104.726 104.726 46.98 104.726 104.726-46.979 104.726-104.726 104.726z"/><path d="m462.148 271.195c-2.503-3.3-7.208-3.945-10.508-1.441s-3.945 7.208-1.441 10.508c8.12 10.701 13.001 23.155 14.343 36.375h-69.162l-48.879-48.879c12.504-10.171 28.435-16.282 45.772-16.282 11.712 0 22.889 2.698 33.221 8.02 3.684 1.897 8.206.449 10.102-3.233 1.897-3.682.449-8.205-3.233-10.102-12.301-6.336-26.164-9.685-40.09-9.685-48.337 0-87.662 39.325-87.662 87.661s39.325 87.661 87.662 87.661c48.336 0 87.661-39.325 87.661-87.661.002-19.299-6.149-37.606-17.786-52.942zm-69.874 125.603c-40.066 0-72.662-32.596-72.662-72.661 0-17.337 6.112-33.269 16.283-45.772l51.076 51.076c1.406 1.406 3.314 2.197 5.303 2.197h72.275c-3.765 36.554-34.741 65.16-72.275 65.16z"/></g></svg>
                              <span class="ml-3">Pending</span>
                            </span>
                          </div>
                        </div>
                      </v-fade-transition>
                    </div>
                  </div>
                </template>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <div>
                  <div>
                    <v-text-field
                      id="DKIMName"
                      :model-value="dkimName"
                      append-icon="mdi-content-copy"
                      density="compact"
                      label="Name"
                      readonly
                      variant="outlined"
                      @click:append="copyToClipboardInit(dkimName)"
                    ></v-text-field>
                    <v-text-field
                      id="DKIMValue"
                      :model-value="dkimValue"
                      append-icon="mdi-content-copy"
                      density="compact"
                      label="Value"
                      readonly
                      variant="outlined"
                      @click:append="copyToClipboardInit(dkimValue)"
                    ></v-text-field>
                    <span v-if="currentWorkingSetting.DKIMUpdateStatus === 'Verified'" class="text-sm text-green-600 flex items-center float-right">
                      <svg enable-background="new 0 0 24 24" height="15" viewBox="0 0 24 24" width="15" xmlns="http://www.w3.org/2000/svg"><path d="m17.5 24c-3.584 0-6.5-2.916-6.5-6.5s2.916-6.5 6.5-6.5 6.5 2.916 6.5 6.5-2.916 6.5-6.5 6.5zm0-11.5c-2.757 0-5 2.243-5 5s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"/><path d="m16.5 20c-.198 0-.39-.079-.53-.22l-2-2c-.293-.293-.293-.768 0-1.061s.768-.293 1.061 0l1.434 1.433 2.972-3.396c.274-.313.747-.343 1.059-.071.312.273.343.747.07 1.058l-3.5 4c-.138.157-.334.25-.541.257-.008 0-.017 0-.025 0z"/><path d="m8.5 21c-.048 0-.096-.004-.143-.014-.084-.016-8.357-1.725-8.357-11.056v-6.18c0-.31.19-.587.479-.699l7.75-3c.174-.067.367-.067.541 0l7.75 3c.29.112.48.389.48.699v4.5c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-3.986l-7-2.709-7 2.709v5.666c0 7.594 6.01 9.32 7.012 9.555h.002c.406-.086.801.182.882.589.081.406-.183.801-.589.882l-.186.034c-.04.007-.08.01-.121.01z"/></svg>
                      Verified
                    </span>
                    <span v-else class="text-sm text-yellow-600 flex items-center float-right">
                      <svg id="Capa_1" enable-background="new 0 0 512 512" height="15" viewBox="0 0 512 512" width="15" xmlns="http://www.w3.org/2000/svg"><g><path d="m392.274 204.411c-37.588 0-71.18 17.417-93.146 44.599l-30.294-29.083 129.032-123.87c1.875 3.468 2.941 7.434 2.941 11.645v72.145c0 4.142 3.358 7.5 7.5 7.5s7.5-3.358 7.5-7.5v-72.145c0-21.816-17.749-39.564-39.565-39.564h-336.678c-21.815-.001-39.564 17.748-39.564 39.564v224.452c0 21.816 17.749 39.564 39.564 39.564h216.436c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5h-216.436c-3.876 0-7.543-.906-10.806-2.511l129.275-124.104c14.57 11.748 32.216 17.637 49.87 17.637s35.3-5.889 49.87-17.637l32.583 31.28c-11.284 18.26-17.808 39.759-17.808 62.755 0 66.017 53.709 119.726 119.726 119.726s119.726-53.71 119.726-119.727-53.708-119.726-119.726-119.726zm-229.14 10.238-16.006-15.366c-2.988-2.869-7.736-2.772-10.604.216s-2.771 7.736.217 10.604l10.232 9.823-129.032 123.872c-1.875-3.468-2.941-7.434-2.941-11.645v-224.451c0-4.211 1.066-8.177 2.941-11.645l93.952 90.194c1.454 1.396 3.325 2.09 5.193 2.09 1.971 0 3.939-.772 5.412-2.306 2.868-2.988 2.771-7.736-.217-10.604l-93.523-89.783c3.263-1.605 6.93-2.511 10.806-2.511h336.677c3.876 0 7.543.906 10.807 2.511l-134.376 129.001c-25.104 24.101-64.433 24.101-89.538 0zm229.14 214.214c-57.746 0-104.726-46.98-104.726-104.726s46.98-104.726 104.726-104.726 104.726 46.98 104.726 104.726-46.979 104.726-104.726 104.726z"/><path d="m462.148 271.195c-2.503-3.3-7.208-3.945-10.508-1.441s-3.945 7.208-1.441 10.508c8.12 10.701 13.001 23.155 14.343 36.375h-69.162l-48.879-48.879c12.504-10.171 28.435-16.282 45.772-16.282 11.712 0 22.889 2.698 33.221 8.02 3.684 1.897 8.206.449 10.102-3.233 1.897-3.682.449-8.205-3.233-10.102-12.301-6.336-26.164-9.685-40.09-9.685-48.337 0-87.662 39.325-87.662 87.661s39.325 87.661 87.662 87.661c48.336 0 87.661-39.325 87.661-87.661.002-19.299-6.149-37.606-17.786-52.942zm-69.874 125.603c-40.066 0-72.662-32.596-72.662-72.661 0-17.337 6.112-33.269 16.283-45.772l51.076 51.076c1.406 1.406 3.314 2.197 5.303 2.197h72.275c-3.765 36.554-34.741 65.16-72.275 65.16z"/></g></svg>
                      Pending
                    </span>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-title>
                <template v-slot:default="{ expanded }">
                  <div>
                    <div class="col-4"><span>SPF</span></div>
                    <div class="col-8 text--secondary">
                      <v-fade-transition leave-absolute>
                        <span v-if="expanded">Type TXT</span>
                        <div v-else style="width: 100%">
                          <div class="col-6">
                            <span v-if="currentWorkingSetting.SPFVerified" class="text-sm text-green-600 flex items-center float-right">
                              <svg enable-background="new 0 0 24 24" height="15" viewBox="0 0 24 24" width="15" xmlns="http://www.w3.org/2000/svg"><path d="m17.5 24c-3.584 0-6.5-2.916-6.5-6.5s2.916-6.5 6.5-6.5 6.5 2.916 6.5 6.5-2.916 6.5-6.5 6.5zm0-11.5c-2.757 0-5 2.243-5 5s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"/><path d="m16.5 20c-.198 0-.39-.079-.53-.22l-2-2c-.293-.293-.293-.768 0-1.061s.768-.293 1.061 0l1.434 1.433 2.972-3.396c.274-.313.747-.343 1.059-.071.312.273.343.747.07 1.058l-3.5 4c-.138.157-.334.25-.541.257-.008 0-.017 0-.025 0z"/><path d="m8.5 21c-.048 0-.096-.004-.143-.014-.084-.016-8.357-1.725-8.357-11.056v-6.18c0-.31.19-.587.479-.699l7.75-3c.174-.067.367-.067.541 0l7.75 3c.29.112.48.389.48.699v4.5c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-3.986l-7-2.709-7 2.709v5.666c0 7.594 6.01 9.32 7.012 9.555h.002c.406-.086.801.182.882.589.081.406-.183.801-.589.882l-.186.034c-.04.007-.08.01-.121.01z"/></svg>
                              <span class="ml-3">Verified</span>
                            </span>
                            <span v-else class="text-sm text-yellow-600 flex items-center float-right">
                              <svg id="Capa_1" enable-background="new 0 0 512 512" height="15" viewBox="0 0 512 512" width="15" xmlns="http://www.w3.org/2000/svg"><g><path d="m392.274 204.411c-37.588 0-71.18 17.417-93.146 44.599l-30.294-29.083 129.032-123.87c1.875 3.468 2.941 7.434 2.941 11.645v72.145c0 4.142 3.358 7.5 7.5 7.5s7.5-3.358 7.5-7.5v-72.145c0-21.816-17.749-39.564-39.565-39.564h-336.678c-21.815-.001-39.564 17.748-39.564 39.564v224.452c0 21.816 17.749 39.564 39.564 39.564h216.436c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5h-216.436c-3.876 0-7.543-.906-10.806-2.511l129.275-124.104c14.57 11.748 32.216 17.637 49.87 17.637s35.3-5.889 49.87-17.637l32.583 31.28c-11.284 18.26-17.808 39.759-17.808 62.755 0 66.017 53.709 119.726 119.726 119.726s119.726-53.71 119.726-119.727-53.708-119.726-119.726-119.726zm-229.14 10.238-16.006-15.366c-2.988-2.869-7.736-2.772-10.604.216s-2.771 7.736.217 10.604l10.232 9.823-129.032 123.872c-1.875-3.468-2.941-7.434-2.941-11.645v-224.451c0-4.211 1.066-8.177 2.941-11.645l93.952 90.194c1.454 1.396 3.325 2.09 5.193 2.09 1.971 0 3.939-.772 5.412-2.306 2.868-2.988 2.771-7.736-.217-10.604l-93.523-89.783c3.263-1.605 6.93-2.511 10.806-2.511h336.677c3.876 0 7.543.906 10.807 2.511l-134.376 129.001c-25.104 24.101-64.433 24.101-89.538 0zm229.14 214.214c-57.746 0-104.726-46.98-104.726-104.726s46.98-104.726 104.726-104.726 104.726 46.98 104.726 104.726-46.979 104.726-104.726 104.726z"/><path d="m462.148 271.195c-2.503-3.3-7.208-3.945-10.508-1.441s-3.945 7.208-1.441 10.508c8.12 10.701 13.001 23.155 14.343 36.375h-69.162l-48.879-48.879c12.504-10.171 28.435-16.282 45.772-16.282 11.712 0 22.889 2.698 33.221 8.02 3.684 1.897 8.206.449 10.102-3.233 1.897-3.682.449-8.205-3.233-10.102-12.301-6.336-26.164-9.685-40.09-9.685-48.337 0-87.662 39.325-87.662 87.661s39.325 87.661 87.662 87.661c48.336 0 87.661-39.325 87.661-87.661.002-19.299-6.149-37.606-17.786-52.942zm-69.874 125.603c-40.066 0-72.662-32.596-72.662-72.661 0-17.337 6.112-33.269 16.283-45.772l51.076 51.076c1.406 1.406 3.314 2.197 5.303 2.197h72.275c-3.765 36.554-34.741 65.16-72.275 65.16z"/></g></svg>
                              <span class="ml-3">Pending</span>
                            </span>
                          </div>
                        </div>
                      </v-fade-transition>
                    </div>
                  </div>
                </template>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-text-field
                  id="SPFName"
                  v-model="currentWorkingSetting.SPFHost"
                  append-icon="mdi-content-copy"
                  density="compact"
                  label="Name"
                  readonly
                  variant="outlined"
                  @click:append="copyToClipboardInit(currentWorkingSetting.SPFHost)"
                ></v-text-field>
                <v-text-field
                  id="SPFValue"
                  v-model="currentWorkingSetting.SPFTextValue"
                  append-icon="mdi-content-copy"
                  density="compact"
                  label="Value"
                  readonly
                  variant="outlined"
                  @click:append="copyToClipboardInit(currentWorkingSetting.SPFTextValue)"
                ></v-text-field>
                <span v-if="currentWorkingSetting.SPFVerified" class="text-sm text-green-600 flex items-center float-right">
                  <svg enable-background="new 0 0 24 24" height="15" viewBox="0 0 24 24" width="15" xmlns="http://www.w3.org/2000/svg"><path d="m17.5 24c-3.584 0-6.5-2.916-6.5-6.5s2.916-6.5 6.5-6.5 6.5 2.916 6.5 6.5-2.916 6.5-6.5 6.5zm0-11.5c-2.757 0-5 2.243-5 5s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"/><path d="m16.5 20c-.198 0-.39-.079-.53-.22l-2-2c-.293-.293-.293-.768 0-1.061s.768-.293 1.061 0l1.434 1.433 2.972-3.396c.274-.313.747-.343 1.059-.071.312.273.343.747.07 1.058l-3.5 4c-.138.157-.334.25-.541.257-.008 0-.017 0-.025 0z"/><path d="m8.5 21c-.048 0-.096-.004-.143-.014-.084-.016-8.357-1.725-8.357-11.056v-6.18c0-.31.19-.587.479-.699l7.75-3c.174-.067.367-.067.541 0l7.75 3c.29.112.48.389.48.699v4.5c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-3.986l-7-2.709-7 2.709v5.666c0 7.594 6.01 9.32 7.012 9.555h.002c.406-.086.801.182.882.589.081.406-.183.801-.589.882l-.186.034c-.04.007-.08.01-.121.01z"/></svg>
                  <span class="ml-3">Verified</span>
                </span>
                <span v-else class="text-sm text-yellow-600 flex items-center float-right">
                  <svg id="Capa_1" enable-background="new 0 0 512 512" height="15" viewBox="0 0 512 512" width="15" xmlns="http://www.w3.org/2000/svg"><g><path d="m392.274 204.411c-37.588 0-71.18 17.417-93.146 44.599l-30.294-29.083 129.032-123.87c1.875 3.468 2.941 7.434 2.941 11.645v72.145c0 4.142 3.358 7.5 7.5 7.5s7.5-3.358 7.5-7.5v-72.145c0-21.816-17.749-39.564-39.565-39.564h-336.678c-21.815-.001-39.564 17.748-39.564 39.564v224.452c0 21.816 17.749 39.564 39.564 39.564h216.436c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5h-216.436c-3.876 0-7.543-.906-10.806-2.511l129.275-124.104c14.57 11.748 32.216 17.637 49.87 17.637s35.3-5.889 49.87-17.637l32.583 31.28c-11.284 18.26-17.808 39.759-17.808 62.755 0 66.017 53.709 119.726 119.726 119.726s119.726-53.71 119.726-119.727-53.708-119.726-119.726-119.726zm-229.14 10.238-16.006-15.366c-2.988-2.869-7.736-2.772-10.604.216s-2.771 7.736.217 10.604l10.232 9.823-129.032 123.872c-1.875-3.468-2.941-7.434-2.941-11.645v-224.451c0-4.211 1.066-8.177 2.941-11.645l93.952 90.194c1.454 1.396 3.325 2.09 5.193 2.09 1.971 0 3.939-.772 5.412-2.306 2.868-2.988 2.771-7.736-.217-10.604l-93.523-89.783c3.263-1.605 6.93-2.511 10.806-2.511h336.677c3.876 0 7.543.906 10.807 2.511l-134.376 129.001c-25.104 24.101-64.433 24.101-89.538 0zm229.14 214.214c-57.746 0-104.726-46.98-104.726-104.726s46.98-104.726 104.726-104.726 104.726 46.98 104.726 104.726-46.979 104.726-104.726 104.726z"/><path d="m462.148 271.195c-2.503-3.3-7.208-3.945-10.508-1.441s-3.945 7.208-1.441 10.508c8.12 10.701 13.001 23.155 14.343 36.375h-69.162l-48.879-48.879c12.504-10.171 28.435-16.282 45.772-16.282 11.712 0 22.889 2.698 33.221 8.02 3.684 1.897 8.206.449 10.102-3.233 1.897-3.682.449-8.205-3.233-10.102-12.301-6.336-26.164-9.685-40.09-9.685-48.337 0-87.662 39.325-87.662 87.661s39.325 87.661 87.662 87.661c48.336 0 87.661-39.325 87.661-87.661.002-19.299-6.149-37.606-17.786-52.942zm-69.874 125.603c-40.066 0-72.662-32.596-72.662-72.661 0-17.337 6.112-33.269 16.283-45.772l51.076 51.076c1.406 1.406 3.314 2.197 5.303 2.197h72.275c-3.765 36.554-34.741 65.16-72.275 65.16z"/></g></svg>
                  <span class="ml-3">Pending</span>
                </span>
              </v-expansion-panel-text>
            </v-expansion-panel>

            <v-expansion-panel>
              <v-expansion-panel-title>
                <template v-slot:default="{ expanded }">
                  <div class="">
                    <div class="col-4"><span>Return-Path</span></div>
                    <div class="col-8 text--secondary">
                      <v-fade-transition leave-absolute>
                        <span v-if="expanded">Type: CNAME</span>
                        <div v-else class="" style="width: 100%">
                          <div class="col-6">
                            <span v-if="currentWorkingSetting.ReturnPathDomainVerified" class="text-sm text-green-600 flex items-center float-right">
                              <svg enable-background="new 0 0 24 24" height="15" viewBox="0 0 24 24" width="15" xmlns="http://www.w3.org/2000/svg"><path d="m17.5 24c-3.584 0-6.5-2.916-6.5-6.5s2.916-6.5 6.5-6.5 6.5 2.916 6.5 6.5-2.916 6.5-6.5 6.5zm0-11.5c-2.757 0-5 2.243-5 5s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"/><path d="m16.5 20c-.198 0-.39-.079-.53-.22l-2-2c-.293-.293-.293-.768 0-1.061s.768-.293 1.061 0l1.434 1.433 2.972-3.396c.274-.313.747-.343 1.059-.071.312.273.343.747.07 1.058l-3.5 4c-.138.157-.334.25-.541.257-.008 0-.017 0-.025 0z"/><path d="m8.5 21c-.048 0-.096-.004-.143-.014-.084-.016-8.357-1.725-8.357-11.056v-6.18c0-.31.19-.587.479-.699l7.75-3c.174-.067.367-.067.541 0l7.75 3c.29.112.48.389.48.699v4.5c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-3.986l-7-2.709-7 2.709v5.666c0 7.594 6.01 9.32 7.012 9.555h.002c.406-.086.801.182.882.589.081.406-.183.801-.589.882l-.186.034c-.04.007-.08.01-.121.01z"/></svg>
                              <span class="ml-3">Verified</span>
                            </span>
                            <span v-else class="text-sm text-yellow-600 flex items-center float-right">
                              <svg id="Capa_1" enable-background="new 0 0 512 512" height="15" viewBox="0 0 512 512" width="15" xmlns="http://www.w3.org/2000/svg"><g><path d="m392.274 204.411c-37.588 0-71.18 17.417-93.146 44.599l-30.294-29.083 129.032-123.87c1.875 3.468 2.941 7.434 2.941 11.645v72.145c0 4.142 3.358 7.5 7.5 7.5s7.5-3.358 7.5-7.5v-72.145c0-21.816-17.749-39.564-39.565-39.564h-336.678c-21.815-.001-39.564 17.748-39.564 39.564v224.452c0 21.816 17.749 39.564 39.564 39.564h216.436c4.142 0 7.5-3.358 7.5-7.5s-3.358-7.5-7.5-7.5h-216.436c-3.876 0-7.543-.906-10.806-2.511l129.275-124.104c14.57 11.748 32.216 17.637 49.87 17.637s35.3-5.889 49.87-17.637l32.583 31.28c-11.284 18.26-17.808 39.759-17.808 62.755 0 66.017 53.709 119.726 119.726 119.726s119.726-53.71 119.726-119.727-53.708-119.726-119.726-119.726zm-229.14 10.238-16.006-15.366c-2.988-2.869-7.736-2.772-10.604.216s-2.771 7.736.217 10.604l10.232 9.823-129.032 123.872c-1.875-3.468-2.941-7.434-2.941-11.645v-224.451c0-4.211 1.066-8.177 2.941-11.645l93.952 90.194c1.454 1.396 3.325 2.09 5.193 2.09 1.971 0 3.939-.772 5.412-2.306 2.868-2.988 2.771-7.736-.217-10.604l-93.523-89.783c3.263-1.605 6.93-2.511 10.806-2.511h336.677c3.876 0 7.543.906 10.807 2.511l-134.376 129.001c-25.104 24.101-64.433 24.101-89.538 0zm229.14 214.214c-57.746 0-104.726-46.98-104.726-104.726s46.98-104.726 104.726-104.726 104.726 46.98 104.726 104.726-46.979 104.726-104.726 104.726z"/><path d="m462.148 271.195c-2.503-3.3-7.208-3.945-10.508-1.441s-3.945 7.208-1.441 10.508c8.12 10.701 13.001 23.155 14.343 36.375h-69.162l-48.879-48.879c12.504-10.171 28.435-16.282 45.772-16.282 11.712 0 22.889 2.698 33.221 8.02 3.684 1.897 8.206.449 10.102-3.233 1.897-3.682.449-8.205-3.233-10.102-12.301-6.336-26.164-9.685-40.09-9.685-48.337 0-87.662 39.325-87.662 87.661s39.325 87.661 87.662 87.661c48.336 0 87.661-39.325 87.661-87.661.002-19.299-6.149-37.606-17.786-52.942zm-69.874 125.603c-40.066 0-72.662-32.596-72.662-72.661 0-17.337 6.112-33.269 16.283-45.772l51.076 51.076c1.406 1.406 3.314 2.197 5.303 2.197h72.275c-3.765 36.554-34.741 65.16-72.275 65.16z"/></g></svg>
                              <span class="ml-3">Pending</span>
                            </span>
                          </div>
                        </div>
                      </v-fade-transition>
                    </div>
                  </div>
                </template>
              </v-expansion-panel-title>
              <v-expansion-panel-text>
                <v-text-field
                  id="CNAMEName"
                  v-model="currentWorkingSetting.ReturnPathDomain"
                  append-icon="mdi-content-copy"
                  density="compact"
                  label="Name"
                  readonly
                  variant="outlined"
                  @click:append="copyToClipboardInit(currentWorkingSetting.ReturnPathDomain)"
                ></v-text-field>
                <v-text-field
                  id="CNAMEValue"
                  v-model="currentWorkingSetting.ReturnPathDomainCNAMEValue"
                  append-icon="mdi-content-copy"
                  density="compact"
                  label="Value"
                  readonly
                  variant="outlined"
                  @click:append="copyToClipboardInit(currentWorkingSetting.ReturnPathDomainCNAMEValue)"
                ></v-text-field>
                <div>
                  <span v-if="currentWorkingSetting.ReturnPathDomainVerified" class="text-sm text-green-600 flex items-center float-right">
                    <svg enable-background="new 0 0 24 24" height="15" viewBox="0 0 24 24" width="15" xmlns="http://www.w3.org/2000/svg"><path d="m17.5 24c-3.584 0-6.5-2.916-6.5-6.5s2.916-6.5 6.5-6.5 6.5 2.916 6.5 6.5-2.916 6.5-6.5 6.5zm0-11.5c-2.757 0-5 2.243-5 5s2.243 5 5 5 5-2.243 5-5-2.243-5-5-5z"/><path d="m16.5 20c-.198 0-.39-.079-.53-.22l-2-2c-.293-.293-.293-.768 0-1.061s.768-.293 1.061 0l1.434 1.433 2.972-3.396c.274-.313.747-.343 1.059-.071.312.273.343.747.07 1.058l-3.5 4c-.138.157-.334.25-.541.257-.008 0-.017 0-.025 0z"/><path d="m8.5 21c-.048 0-.096-.004-.143-.014-.084-.016-8.357-1.725-8.357-11.056v-6.18c0-.31.19-.587.479-.699l7.75-3c.174-.067.367-.067.541 0l7.75 3c.29.112.48.389.48.699v4.5c0 .414-.336.75-.75.75s-.75-.336-.75-.75v-3.986l-7-2.709-7 2.709v5.666c0 7.594 6.01 9.32 7.012 9.555h.002c.406-.086.801.182.882.589.081.406-.183.801-.589.882l-.186.034c-.04.007-.08.01-.121.01z"/></svg>
                    Verified
                  </span>
                  <div v-else>
                    <div class="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded relative" role="alert">
                      <strong class="font-bold mr-2">Pending...</strong>
                      <span class="block sm:inline">We couldn’t find your Return-Path record. Go back to your DNS provider and double check that the hostname is set to pm-bounces. If the hostname and value look good, then it might take up to 48 hours for the DNS changes to propagate.</span><br>
                      <v-btn :loading="emailDomainSettingsLoading.checkingReturnPathStatus" class="mt-2" @click="emailDomainCheckReturnPathStatus(currentWorkingSetting)">
                        Re-check
                      </v-btn>
                    </div>
                  </div>
                </div>
              </v-expansion-panel-text>
            </v-expansion-panel>
          </v-expansion-panels>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="domainSettingsDeleteConfirmDialog" max-width="290">
      <v-card :disabled="emailDomainSettingsLoading.deletingEmailDomain">
        <v-card-title class="text-h5">Remove Domain</v-card-title>
        <v-card-text>
          Are you sure you want to remove this domain?
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn color="grey-darken-1" size="small" variant="text" @click="domainSettingsDeleteConfirmDialog = false">
            Nevermind
          </v-btn>
          <v-btn :loading="emailDomainSettingsLoading.deletingEmailDomain" color="red text-white" @click="deleteEmailDomain(currentWorkingSetting)">
            Delete
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-snackbar
      v-model="copiedText"
      class="elevation-24"
      color="accent-4"
      location="bottom"
      width="100"
    >
      Copied!
    </v-snackbar>
  </div>
</template>

<script lang="ts">
import {ref, watch} from 'vue';
import {EventBus} from '@/plugins/eventBus';
import {clone, filter, get, split, toLower} from 'lodash';
import {copyTextToClipboard} from '@/helpers';
import EmailTemplates from '@/components/dashboard/settings/Emails/EmailTemplates.vue';
import {useEmailDomainSettings} from '@/composables/useEmailDomainSettings';
import {EmailDomainSettings} from '@/store/modules/email-domain-settings';


export default {
  name: 'SettingsEmail',

  components: {
    EmailTemplates,
  },

  setup() {
    const {
      emailDomainSettingsLoading,
      emailDomainSettings,
      fetchEmailDomain,
      createEmailDomain,
      deleteEmailDomain,
      emailDomainCheckDKIM,
      emailDomainCheckReturnPathStatus,
      setAsDefaultSendingDomain,
      sendTestEmail,
      updateEmailDomainUsername
    } = useEmailDomainSettings();

    const editEmail = ref(null);
    const copiedText = ref(false);
    const domainDialog = ref(false);
    const testEmailDialog = ref(false);
    const domainSettingsDialog = ref(false);
    const newDomain = ref<string>('');
    const testEmailAddress = ref('');
    const createEmailDomainErrorMessage = ref('');
    const currentWorkingSetting = ref<EmailDomainSettings>(null);
    const domainSettingsDeleteConfirmDialog = ref(false);

    watch(newDomain, (newValue) => {
      newDomain.value = newValue.toLowerCase();
    });

    return {
      editEmail,
      copiedText,
      domainDialog,
      testEmailDialog,
      domainSettingsDialog,
      newDomain,
      testEmailAddress,
      createEmailDomainErrorMessage,
      currentWorkingSetting,
      domainSettingsDeleteConfirmDialog,
      emailDomainSettingsLoading,
      emailDomainSettings,
      fetchEmailDomain,
      createEmailDomain,
      deleteEmailDomain,
      emailDomainCheckDKIM,
      emailDomainCheckReturnPathStatus,
      setAsDefaultSendingDomain,
      sendTestEmail,
      updateEmailDomainUsername,
    }
  },

  created() {
    EventBus.$on('create-email-domain-success', () => {
      const newDomain = this.domains.find((d) => toLower(d.domain) === toLower(this.newDomain));
      this.newDomain = '';
      this.domainDialog = false;

      this.domainSettingsInit(newDomain);
    });
    EventBus.$on('delete-email-domain-success', () => {
      this.currentWorkingSetting = '';
      this.domainSettingsDeleteConfirmDialog = false;
    });
    EventBus.$on('send-test-email-success', () => {
      this.testEmailAddress = '';
      this.testEmailDialog = false;
    });
    EventBus.$on('create-email-domain-error', (message) => {
      this.createEmailDomainErrorMessage = message;
      this.testEmailDialog = false;
    });

    this.fetchEmailDomain();
  },

  computed: {
    domains() {
      const domains = clone(this.emailDomainSettings);
      domains.push({
        domain: 'fleetwire.io',
        email: 'no-reply@fleetwire.io',
        DKIMVerified: true,
        SPFVerified: true,
      });
      return domains;
    },
    activeDomain() {
      const domain = filter(this.domains, (d) => d.active);
      return get(domain, '0.domain', 'fleetwire.io');
    },
    dkimName() {
      if (get(this.currentWorkingSetting, 'DKIMPendingHost')) return this.currentWorkingSetting.DKIMPendingHost;
      if (get(this.currentWorkingSetting, 'DKIMHost')) return this.currentWorkingSetting.DKIMHost;
      return '';
    },
    dkimValue() {
      if (get(this.currentWorkingSetting, 'DKIMPendingTextValue')) return this.currentWorkingSetting.DKIMPendingTextValue;
      if (get(this.currentWorkingSetting, 'DKIMTextValue')) return this.currentWorkingSetting.DKIMTextValue;
      return '';
    },
    username: {
      get() {
        return split(this.editEmail.email, '@')[0];
      },
      set(value) {
        const domain = split(this.editEmail.email, '@');
        this.editEmail.email = `${value}@${domain[1]}`;
      },
    },
  },

  methods: {

    updateEmailAddress(item) {
      this.editEmail = null;
      this.updateEmailDomainUsername(item);
    },

    sendTestEmailInit() {
      this.testEmailDialog = true;
    },

    copyToClipboardInit(text) {
      copyTextToClipboard(text)
        .then(() => {
          this.variableLegendDialog = false;
          this.copiedText = true;
        });
    },

    showSetAsDefault(item) {
      if (this.domains.length === 1) return false;
      if (item.active) return false;

      if (!item.SPFVerified || !item.DKIMVerified) return false;

      const found = filter(this.domains, (d) => d.active);

      return !(found.length === 0 && item.domain === 'fleetwire.io');
    },

    showSending(item) {
      if (this.domains.length === 1) return true;
      if (item.active) return true;

      const found = filter(this.domains, (d) => d.active);

      return found.length === 0 && item.domain === 'fleetwire.io';
    },

    domainSettingsInit(item) {
      this.currentWorkingSetting = item;
      this.domainSettingsDialog = true;
    },

    domainSettingsRemoveInit(item) {
      this.currentWorkingSetting = item;
      this.domainSettingsDeleteConfirmDialog = true;
    },
  },

  beforeUnmount() {
    EventBus.$off('create-email-domain-success');
    EventBus.$off('delete-email-domain-success');
    EventBus.$off('send-test-email-success');
    EventBus.$off('create-email-domain-error');
  },
};
</script>
